import { filter, head, map, pipe, pluck, toArray, toAsync } from '@fxts/core';
import { selectedOptionGroupUtil } from '@modules/BpOption/S/Function/selected-option-group.util.ts';
import { makeCanvasByUrl } from '@modules/Canvas/S/util';
import {
  swiperProductDetailPreview,
  swiperProductDetailPreviewMo,
} from '@modules/MShop/App/ProductDetail/F/Mui/handles';
import {
  makeProdImageSlideHtmlNess,
  makeThumbSwiperHtmlNess,
} from '@modules/MShop/App/ProductDetail/S/Tmpl/partials/productDetailContent';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import type { FrameCharacter, NessFrameDetails } from '@modules/NessLabs/Frame/S';
import { NessLabsFrameS } from '@modules/NessLabs/Frame/S';
import { CanvasUtils } from '@modules/NessLabs/S/Function/canvas.utils';
import { getScaleUpCanvasPixelatedWithCanvas } from '@modules/NessLabs/S/Function/images';
import createCharacterNameTagCanvas from '@modules/NessLabs/F/Function/ness-frame-sub-functions/character-name-tag';
import { NessLabsF } from '@modules/NessLabs/F/Function/module/NessLabsF';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { getJustifyCanvas } from '@modules/Maker/F/canvas_trim';

export const loadProductDetailForFrame = async (el: Element) => {
  const { product_color, selected_option_group } = box().product_detail;
  const details: NessFrameDetails = selected_option_group.details;
  const frame_characters = details?.value?.frame_characters || [];
  const character_urls = frame_characters.map((character) => ({
    url: character.maple_image_url.replace('amp;', ''),
    is_thumb: false,
  }));
  const character_names = frame_characters.map((character) => character.gc_name);

  // 1. 캐릭터 캔버스 생성
  const character_canvases = await makeCharacterCanvases(character_urls);
  const character_name_canvases = await makeTextCanvases(
    character_names,
    NessLabsFrameS.NessFrameProduct.canvas_option.text,
  );
  // 2. 캐릭터 우선순위 조정
  const first_layer_character_canvases = character_canvases.filter((_, i) => i % 2 === 0);
  const first_layer_text_canvases = character_name_canvases.filter((_, i) => i % 2 === 0);
  const second_layer_character_canvases = character_canvases.filter((_, i) => i % 2 === 1);
  const second_layer_text_canvases = character_name_canvases.filter((_, i) => i % 2 === 1);
  // 2+ 캐릭터 비면 hollow 이미지가 담긴 캔버스 넣기
  const hollow_canvas = (await makeCharacterCanvas(
    NessLabsFrameS.NessFrameProduct.canvas_option.images.character.hollow_character_image_url,
  )) as HTMLCanvasElement;
  while (first_layer_character_canvases.length < 5) {
    first_layer_character_canvases.push(hollow_canvas);
  }
  while (second_layer_character_canvases.length < 5) {
    second_layer_character_canvases.push(hollow_canvas);
  }
  // 3. 프레임 캔버스 생성 + 프레임 케릭터와 캔버스 캐릭터 합성
  const frame_canvas = await makeFrameCanvas(
    first_layer_character_canvases,
    second_layer_character_canvases,
    first_layer_text_canvases,
    second_layer_text_canvases,
    getFrameImageUrl(),
    'interleaved',
    /* 기획상 왼쪽 위부터 순서대로 레이어가 쌓이는것으로 변경. 기존 로직 수정하지 않은채로 교차해서 레이어 배치 되도록 수정*/
  );

  // 워닝이 필요한 캐릭터 정보 추출
  const warningCharacters = frame_characters
    .map((character, index) => {
      const hasWarning =
        character?.checker?.warning?.width === true || character?.checker?.warning?.height === true;
      if (hasWarning) {
        return {
          characterIndex: index,
          character,
        };
      }
      return null;
    })
    .filter((item) => item !== null);
  // 4. 프레임 캔버스 -> 이미지
  const frame_image_url = frame_canvas.toDataURL('image/png');

  const product_detail = {
    ...product_color,
    thumbnail_ratio: box.sel('product_detail')?.base_product?.thumbnail_ratio,
    _: { stores_product: box.sel('product_detail')?.stores_product },
    membership_info: box.sel('product_detail')?.membership_info,
    thumbnails: makeThumbnails(
      frame_image_url,
      character_canvases
        // .map((c) => moveDesignInCanvas(c, { x: 0, y: -112 }))
        .map((c) => getJustifyCanvas(c, 150))
        .map((c) => ({ url: c.toDataURL('image/png'), is_thumb: false })),
    ),
    nessProductDetail: box.sel('product_detail')?.nessProductDetail,
  };

  const preview_el = el.querySelector('.pd-contents__preview');
  if (!preview_el) throw new Error('스와이퍼가 존재하지 않습니다.');

  if (MShopUtilF.isMobile()) {
    preview_el.innerHTML = makeProdImageSlideHtmlNess({ product_detail });
    swiperProductDetailPreviewMo();
  } else {
    preview_el.innerHTML = makeThumbSwiperHtmlNess({ product_detail });
    swiperProductDetailPreview();
  }

  // 워닝 호버 요소 추가
  addWarningHoverElements(preview_el, warningCharacters);
  NessLabsF.labs_loader.end();
};

/**
 * 캔버스의 좌우 여백을 트림하고, 원본 캔버스 크기의 중앙에 위치시킨 캔버스를 반환합니다.
 *
 * @param {HTMLCanvasElement} canvas - 트림할 원본 캔버스
 * @returns {HTMLCanvasElement} 좌우 여백이 트림되고 중앙에 배치된 새로운 캔버스
 *
 * @description
 * 1. 좌우 불투명 영역을 감지하여 좌우 여백을 제거
 * 2. 트림된 이미지를 원본 캔버스와 동일한 크기의 새 캔버스 중앙에 배치
 * 3. 상하는 트림하지 않고 유지
 */
function getTrimAndCenterizedCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context 생성 실패');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { width, height, data } = imageData;

  let left = width;
  let right = 0;

  // 좌우 불투명 영역 찾기 (알파값 > 1인 픽셀)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 1) {
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }

  // 불투명 픽셀이 없는 경우 원본 반환
  if (left > right) {
    return canvas;
  }

  // 트림된 너비 계산
  const trimmedWidth = right - left + 1;

  // 원본 크기와 동일한 새 캔버스 생성
  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = canvas.width;
  resultCanvas.height = canvas.height;
  const resultCtx = resultCanvas.getContext('2d');

  if (!resultCtx) {
    throw new Error('Result canvas context 생성 실패');
  }

  // 트림된 이미지를 중앙에 배치
  // x 좌표: (원본 너비 - 트림된 너비) / 2
  const centerX = (canvas.width - trimmedWidth) / 2;

  // 트림된 영역을 중앙에 그리기 (상하는 그대로 유지, 좌우만 중앙 정렬)
  resultCtx.drawImage(
    canvas,
    left, // 소스 x
    0, // 소스 y (상하는 트림하지 않음)
    trimmedWidth, // 소스 width (트림된 너비)
    height, // 소스 height (전체 높이)
    centerX, // 대상 x (중앙)
    0, // 대상 y
    trimmedWidth, // 대상 width
    height, // 대상 height
  );

  return resultCanvas;
}

async function makeCharacterCanvases(image_urls: { url: string }[]) {
  return pipe(
    image_urls,
    pluck('url'),
    toAsync,
    map(makeCharacterCanvas),
    filter((c) => !!c),
    toArray,
  );
}

async function makeTextCanvases(
  texts: string[],
  options: typeof NessLabsFrameS.NessFrameProduct.canvas_option.text,
): Promise<HTMLCanvasElement[]> {
  if (texts.length === 0) return [];

  try {
    const fontFace = new FontFace(options.font_family, `url(${options.font_url})`);
    await fontFace.load();
    document.fonts.add(fontFace);
  } catch (e) {
    console.warn(`${options.font_family} 폰트 로딩 실패, 기본 폰트 사용`);
  }
  return pipe(
    texts,
    toAsync,
    map((text) => createCharacterNameTagCanvas(text, options)),
    toArray,
  );
}

/**
 * Creates a character canvas from an image URL with given options.
 *
 * @async
 * @param {string} image_url - 캐릭터 이미지의 URL
 * @param {Object} [options] - 이미지 변환 옵션
 * @param {Object} [options.move_position] - 이미지 이동 좌표
 * @param {number} [options.move_position.x] - X축 이동 값
 * @param {number} [options.move_position.y] - Y축 이동 값
 * @param {Object} [options.crop_size] - 크롭 영역 크기
 * @param {number} [options.crop_size.width] - 크롭 영역의 너비
 * @param {number} [options.crop_size.height] - 크롭 영역의 높이
 * @param {number} [options.scale_size] - 최종 스케일링 크기 (너비 기준)
 * @returns {Promise<HTMLCanvasElement>} 생성된 캐릭터 캔버스 객체를 반환
 */
export async function makeCharacterCanvas(
  image_url: string,
  options = {
    crop_start_position: NessLabsFrameS.NessFrameProduct.canvas_option.character.crop_start_position,
    move_position: NessLabsFrameS.NessFrameProduct.canvas_option.character.move_position,
    crop_size: {
      width: NessLabsFrameS.NessFrameProduct.canvas_option.character.crop_size.w,
      height: NessLabsFrameS.NessFrameProduct.canvas_option.character.crop_size.h,
    },
    scale_size: NessLabsFrameS.NessFrameProduct.canvas_option.character.scale_size.w,
  },
) {
  const { crop_start_position, move_position, crop_size, scale_size } = options;

  return pipe(
    [await makeCanvasByUrl(image_url)] as HTMLCanvasElement[],
    toAsync,
    map((c) =>
      CanvasUtils.cropCanvas({
        canvas: c,
        bounds: {
          x: crop_start_position.x + move_position.x,
          y: crop_start_position.y + move_position.y,
          width: crop_size.width,
          height: crop_size.height,
        },
        isPixelated: true,
      }),
    ),
    map((c) => {
      return getScaleUpCanvasPixelatedWithCanvas(c, scale_size);
    }),
    toArray,
    head,
  );
}

interface WarningCharacter {
  characterIndex: number;
  character: FrameCharacter;
}

/**
 * 워닝 메시지를 생성하는 헬퍼 함수
 * 캐릭터의 폭(width)과 높이(height) 경고 상태에 따라 안내 문구를 반환합니다.
 */
function createWarningMessages(character: FrameCharacter): string | null {
  const warning = character?.checker?.warning;
  if (!warning) return null;

  const { width, height } = warning;

  if (width && height) {
    return '해당 캐릭터는 세로로 길어 상단 배치를 권장하며, 폭이 넓어 다른 캐릭터를 가릴 수 있습니다. 조립 시 뒤쪽 홈에 끼우는 것을 추천드립니다.';
  }

  if (width) {
    return '해당 캐릭터는 폭이 넓어 다른 캐릭터를 가릴 수 있습니다. 상품 조립 시 앞쪽이 아닌 뒤쪽 홈에 끼우는 것을 추천드립니다.';
  }

  if (height) {
    return '해당 캐릭터는 세로 길이가 길어 상품 조립 시 액자 위층에만 배치하기를 권장합니다.';
  }

  return null;
}
/**
 * 워닝 아이콘과 툴팁을 생성하는 헬퍼 함수
 */
function createWarningIcon(character: FrameCharacter): HTMLDivElement {
  const warningEl = document.createElement('div');
  warningEl.className = 'ness-frame-warning-icon';

  const message = createWarningMessages(character);
  if (message) {
    tippy(warningEl, {
      content: message,
      theme: 'ness-frame-warning',
      placement: 'bottom',
      arrow: false,
      maxWidth: 208,
      zIndex: 999999,
      trigger: 'click',
    });
  }

  return warningEl;
}

/**
 * 그리드 셀을 생성하는 헬퍼 함수
 */
function createGridCell(index: number, warningCharacters: WarningCharacter[]): HTMLDivElement {
  const gridCell = document.createElement('div');
  gridCell.className = 'ness-frame-grid-cell';

  const warningChar = warningCharacters.find((w) => w.characterIndex === index);
  if (warningChar) {
    gridCell.appendChild(createWarningIcon(warningChar.character));
  }

  return gridCell;
}

/**
 * 프레임 캔버스를 생성하고 캐릭터와 텍스트를 합성합니다.
 *
 * @param {HTMLCanvasElement[]} first_layer_canvas - 첫 번째 레이어 캐릭터 캔버스 배열
 * @param {HTMLCanvasElement[]} second_layer_canvas - 두 번째 레이어 캐릭터 캔버스 배열
 * @param {HTMLCanvasElement[]} first_layer_text_canvases - 첫 번째 레이어 텍스트 캔버스 배열
 * @param {HTMLCanvasElement[]} second_layer_text_canvases - 두 번째 레이어 텍스트 캔버스 배열
 * @param {{ image: string; top_image?: string }} frame_images - 프레임 이미지 URL
 * @param {'layer-by-layer' | 'interleaved'} [drawMode='layer-by-layer'] - 레이어 그리기 순서 모드
 *   - 'layer-by-layer': first 레이어 모두 그린 후 → second 레이어 모두 그림 (기본값)
 *   - 'interleaved': first[0] → second[0] → first[1] → second[1] 순서로 교차 그리기
 * @returns {Promise<HTMLCanvasElement>} 완성된 프레임 캔버스
 */
async function makeFrameCanvas(
  first_layer_canvas: HTMLCanvasElement[],
  second_layer_canvas: HTMLCanvasElement[],
  first_layer_text_canvases: HTMLCanvasElement[],
  second_layer_text_canvases: HTMLCanvasElement[],
  { image: frame_image, top_image }: { image: string; top_image?: string },
  drawMode: 'layer-by-layer' | 'interleaved' = 'layer-by-layer',
): Promise<HTMLCanvasElement> {
  const canvas = pipe([await makeCanvasByUrl(frame_image)] as HTMLCanvasElement[], toArray)[0];
  if (!canvas) {
    throw new Error('Frame canvas 생성 실패');
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context 생성 실패');
  }

  const child_canvas = first_layer_canvas[0] || second_layer_canvas[0];

  if (!child_canvas) {
    return canvas; // 아무 캔버스도 없으면 배경만 그려진 캔버스 반환
  }

  const offsets = {
    child: {
      percent: {
        width: ((child_canvas.width / canvas.width) * 100) / 2, // 자식 캔버스 너비의 50% 비율
        height: ((child_canvas.height / canvas.height) * 100) / 2, // 자식 캔버스 높이의 50% 비율
      },
    },
    frame: NessLabsFrameS.NessFrameProduct.canvas_option.frame.offsets,
  };
  const first_layer_character_positions = [
    {
      // 좌좌 상
      x: (canvas.width * (50 - offsets.child.percent.width - 2 * offsets.frame.x)) / 100,
      y: offsets.frame.y_first,
    },
    {
      // 상
      x: (canvas.width * (50 - offsets.child.percent.width)) / 100,
      y: offsets.frame.y_first,
    },
    {
      // 우우 상
      x: (canvas.width * (50 - offsets.child.percent.width + 2 * offsets.frame.x)) / 100,
      y: offsets.frame.y_first,
    },
    {
      // 좌 하
      x: (canvas.width * (50 - offsets.child.percent.width - 1 * offsets.frame.x)) / 100,
      y: offsets.frame.y_second,
    },
    {
      // 우 하
      x: (canvas.width * (50 - offsets.child.percent.width + 1 * offsets.frame.x)) / 100,
      y: offsets.frame.y_second,
    },
  ];

  const second_layer_character_positions = [
    {
      // 좌 상
      x: (canvas.width * (50 - offsets.child.percent.width - 1 * offsets.frame.x)) / 100,
      y: offsets.frame.y_first,
    },
    {
      // 우 상
      x: (canvas.width * (50 - offsets.child.percent.width + 1 * offsets.frame.x)) / 100,
      y: offsets.frame.y_first,
    },
    {
      // 좌좌 하
      x: (canvas.width * (50 - offsets.child.percent.width - 2 * offsets.frame.x)) / 100,
      y: offsets.frame.y_second,
    },
    {
      // 하
      x: (canvas.width * (50 - offsets.child.percent.width)) / 100,
      y: offsets.frame.y_second,
    },
    {
      // 우우 하
      x: (canvas.width * (50 - offsets.child.percent.width + 2 * offsets.frame.x)) / 100,
      y: offsets.frame.y_second,
    },
  ];

  /**
   * 캐릭터와 텍스트를 그리는 헬퍼 함수
   */
  const drawCharacterWithText = (
    character_canvas: HTMLCanvasElement,
    text_canvas: HTMLCanvasElement | undefined,
    position: { x: number; y: number },
  ) => {
    // 캐릭터 그리기
    ctx.drawImage(character_canvas, position.x, position.y, character_canvas.width, character_canvas.height);

    // 텍스트 그리기
    if (text_canvas) {
      const text_x = position.x + (character_canvas.width / 2 - text_canvas.width / 2);
      ctx.drawImage(
        text_canvas,
        text_x,
        position.y + child_canvas.height + 10,
        text_canvas.width,
        text_canvas.height,
      );
    }
  };

  if (drawMode === 'interleaved') {
    // 교차 모드: first[0] → second[0] → first[1] → second[1] ...
    const maxLength = Math.max(first_layer_canvas.length, second_layer_canvas.length);

    for (let i = 0; i < maxLength; i++) {
      // first layer 그리기
      if (i < first_layer_canvas.length) {
        const position = first_layer_character_positions[i] || { x: 0, y: 0 };
        drawCharacterWithText(first_layer_canvas[i]!, first_layer_text_canvases[i], position);
      }

      // second layer 그리기
      if (i < second_layer_canvas.length) {
        const position = second_layer_character_positions[i] || { x: 0, y: 0 };
        drawCharacterWithText(second_layer_canvas[i]!, second_layer_text_canvases[i], position);
      }
    }
  } else {
    // layer-by-layer 모드 (기본): first 레이어 모두 → second 레이어 모두

    // draw first layer
    first_layer_canvas.forEach((c, i) => {
      const position = first_layer_character_positions[i] || { x: 0, y: 0 };
      drawCharacterWithText(c, first_layer_text_canvases[i], position);
    });

    // draw second layer
    second_layer_canvas.forEach((c, i) => {
      const position = second_layer_character_positions[i] || { x: 0, y: 0 };
      drawCharacterWithText(c, second_layer_text_canvases[i], position);
    });
  }

  // 상단 레이어 그리기
  if (top_image) {
    const img = await makeCanvasByUrl(top_image);
    ctx.drawImage(img, 0, 0);
  }

  return canvas;
}

function makeThumbnails(
  frame_url: string,
  character_urls: { url: string; is_thumb: boolean }[],
): { value: { url: string }[] } {
  const thumbnails = {
    value: [{ url: frame_url, is_thumb: true }, ...character_urls],
  };
  return thumbnails;
}

function getFrameImageUrl(): { image: string; top_image?: string } {
  const {
    selected_option_group,
    base_product: {
      _: { bp_option_groups },
    },
  } = box().product_detail;
  const is_frame_selected =
    selectedOptionGroupUtil.getSelectedOptionGroupAndOptionByDevName(
      bp_option_groups,
      selected_option_group.bp_option_ids,
      'NESS_FRAME',
    )?.bp_option?.dev_name === NessLabsFrameS.NessFrameProduct.BP_OPTIONS.NESS_FRAME_SELECTED;
  if (is_frame_selected) {
    return {
      image: NessLabsFrameS.NessFrameProduct.canvas_option.images.frame.with_frame,
      top_image: NessLabsFrameS.NessFrameProduct.canvas_option.images.frame.top_frame_layer,
    };
  } else {
    return { image: NessLabsFrameS.NessFrameProduct.canvas_option.images.frame.without_frame };
  }
}

/**
 * 프레임 이미지에 워닝 호버 요소를 오버레이하는 함수
 */
function addWarningHoverElements(preview_el: Element, warningCharacters: WarningCharacter[]): void {
  const bgWrap = preview_el.querySelector('.swiper-wrapper .mp-product-img-bg-wrap');
  if (!bgWrap) return;

  const img = bgWrap.querySelector('img') as HTMLImageElement;
  if (!img) return;

  let abortController: AbortController | null = null;

  const setupWarnings = () => {
    // 이전 리스너 정리
    abortController?.abort();
    abortController = new AbortController();
    const { signal } = abortController;

    // 기존 wrapper 제거
    bgWrap.querySelector('.ness-frame-warning-wrapper')?.remove();

    // wrapper 생성 및 그리드 셀 추가
    const wrapper = document.createElement('div');
    wrapper.className = 'ness-frame-warning-wrapper';

    const gridCells = pipe(
      Array.from({ length: 10 }, (_, i) => i),
      map((i) => createGridCell(i, warningCharacters)),
      toArray,
    );

    gridCells.forEach((cell) => wrapper.appendChild(cell));
    bgWrap.appendChild(wrapper);

    // 스크롤/스와이프 시 툴팁 숨기기
    const warningIcons = wrapper.querySelectorAll('.ness-frame-warning-icon');
    const tippyInstances = Array.from(warningIcons)
      .map((icon) => (icon as any)._tippy)
      .filter(Boolean);

    if (tippyInstances.length > 0) {
      const hideAllTooltips = () => tippyInstances.forEach((instance) => instance.hide());

      // 스크롤 시 숨기기
      window.addEventListener('scroll', hideAllTooltips, { capture: true, signal });

      // 스와이프/클릭 시 숨기기
      const swiperContainer = preview_el.querySelector('.swiper-container') as HTMLElement & { swiper?: any };
      const swiper = swiperContainer?.swiper;
      if (swiper) {
        const handleClick = (e: PointerEvent) => {
          const target = e.target as HTMLElement;
          if (!target.closest('.ness-frame-warning-icon')) {
            hideAllTooltips();
          }
        };

        swiper.on('touchMove', hideAllTooltips);
        swiper.on('slideChange', hideAllTooltips);
        swiper.on('click', handleClick);

        // AbortController로 swiper 이벤트도 정리
        signal.addEventListener('abort', () => {
          swiper.off('touchMove', hideAllTooltips);
          swiper.off('slideChange', hideAllTooltips);
          swiper.off('click', handleClick);
        });
      }
    }
  };

  // 이미지 로드 상태에 따라 실행
  if (img.complete && img.naturalHeight !== 0) {
    setupWarnings();
  } else {
    img.addEventListener('load', setupWarnings);
  }
}
